import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import {
  LEASE_PLACEMENTS,
  formatDate,
  type FieldCoords,
  type LeasePlacements,
} from "./signaturePlacement";

export interface ExhibitAKnownContact {
  name: string;
  phone: string;
  relationship: string | null;
}

export interface ExhibitAInput {
  client: {
    fullName: string;
    dateOfBirth: string;
    addressLine: string;
    city: string;
    state: string;
    zipcode: string;
    phone: string;
    email: string;
    /** Optional court case number — blank means leave the slot empty. */
    caseNumber: string | null;
  };
  probationOfficer: {
    uid: string | null;
    name: string;
    phone: string | null;
  };
  work: {
    employerName: string;
    employerAddress: string;
    employerPhone: string;
    supervisorName: string;
    supervisorPhone: string;
  };
  /** 1-4 contacts. First 2 are stamped on the PDF; remaining ones live in Firestore. */
  knownContacts: ExhibitAKnownContact[];
}

export interface SignLeaseInput {
  pdfBytes: ArrayBuffer | Uint8Array;
  signaturePngBytes: Uint8Array;
  signerFullName: string;
  exhibitA: ExhibitAInput;
  signedDate?: Date;
  placements?: LeasePlacements;
}

export interface SignLeaseOutput {
  pdfBytes: Uint8Array;
  pdfBase64: string;
}

/**
 * Loads the generated lease template and stamps every blank — both the
 * acknowledgement / signature blocks and the original-layout fields (page 1
 * top table, page 2 lessee name, page 4 "and by ___" name, and the full
 * Exhibit A demographic schedule on page 5).
 *
 * Case number is optional (stamps only if provided). Known contacts: the
 * first 2 entries are stamped onto the page-5 contact rows; additional
 * entries (up to 4 total) are still captured in Firestore via the API
 * payload but are not rendered on the lease PDF.
 *
 * Returns signed PDF bytes plus base64 for upload via callable function.
 */
export async function signLeasePdf(
  input: SignLeaseInput,
): Promise<SignLeaseOutput> {
  const placements = input.placements ?? LEASE_PLACEMENTS;
  const date = input.signedDate ?? new Date();
  const dateLabel = formatDate(date);
  const exhibit = input.exhibitA;

  const pdfDoc = await PDFDocument.load(input.pdfBytes);
  const pages = pdfDoc.getPages();
  if (pages.length === 0) throw new Error("Lease PDF has no pages");

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const sigImage = await pdfDoc.embedPng(input.signaturePngBytes);

  const stampText = (
    coords: FieldCoords,
    text: string,
    opts?: { bold?: boolean; size?: number; maxWidth?: number },
  ) => {
    if (!text) return;
    const page = pickPage(pages, coords.pageIndex);
    const size = opts?.size ?? coords.fontSize ?? 10;
    let value = text;
    if (opts?.maxWidth) {
      value = clipToWidth(
        value,
        size,
        opts?.bold ? fontBold : font,
        opts.maxWidth,
      );
    }
    page.drawText(value, {
      x: coords.x,
      y: coords.y,
      size,
      font: opts?.bold ? fontBold : font,
      color: rgb(0, 0, 0),
    });
  };

  const stampSignature = (coords: FieldCoords) => {
    if (coords.width == null || coords.height == null) {
      throw new Error("Signature placement missing width/height");
    }
    const page = pickPage(pages, coords.pageIndex);
    page.drawImage(sigImage, {
      x: coords.x,
      y: coords.y,
      width: coords.width,
      height: coords.height,
    });
  };

  const fullAddress = joinNonEmpty(
    [
      exhibit.client.addressLine,
      [exhibit.client.city, exhibit.client.state]
        .filter(Boolean)
        .join(", "),
      exhibit.client.zipcode,
    ],
    ", ",
  );
  const shortAddress = exhibit.client.addressLine || fullAddress;

  // Page 1 — top client info table
  stampText(placements.page1TopClientName, input.signerFullName, {
    maxWidth: 200,
  });
  stampText(placements.page1TopDob, exhibit.client.dateOfBirth);
  stampText(placements.page1TopAddress, fullAddress, { maxWidth: 235 });
  stampText(placements.page1TopPhone, exhibit.client.phone);

  // Page 1 — acknowledgement
  stampText(placements.page1ClientName, input.signerFullName);
  stampSignature(placements.page1Signature);
  stampText(placements.page1Date, dateLabel);

  // Page 2 — effective date + lessee name
  stampText(placements.page2EffectiveDate, dateLabel);
  stampText(placements.page2LesseeName, input.signerFullName);

  // Page 4 — "and by ___" + final signature
  stampText(placements.page4AndByName, input.signerFullName);
  stampSignature(placements.page4Signature);
  stampText(placements.page4Date, dateLabel);

  // Page 5 — Exhibit A demographic schedule
  stampText(placements.page5ClientName, input.signerFullName, {
    maxWidth: 130,
  });
  stampText(placements.page5ClientAddress, shortAddress, { maxWidth: 130 });
  stampText(placements.page5ClientZip, exhibit.client.zipcode);
  if (exhibit.client.caseNumber) {
    stampText(placements.page5CaseNumber, exhibit.client.caseNumber, {
      maxWidth: 110,
    });
  }
  stampText(placements.page5Email, exhibit.client.email, { maxWidth: 350 });

  stampText(placements.page5ProbationOfficer, exhibit.probationOfficer.name, {
    maxWidth: 195,
  });

  stampText(placements.page5WorkEmployer, exhibit.work.employerName, {
    maxWidth: 130,
  });
  stampText(placements.page5WorkAddress, exhibit.work.employerAddress, {
    maxWidth: 260,
  });
  stampText(placements.page5WorkPhone, exhibit.work.employerPhone);
  stampText(placements.page5Supervisor, exhibit.work.supervisorName, {
    maxWidth: 145,
  });
  stampText(placements.page5SupervisorPhone, exhibit.work.supervisorPhone);

  // Known contacts (1-2 stamped on the PDF). Additional contacts beyond
  // slot 2 are still persisted in Firestore via the API payload.
  const contactSlots: Array<{
    name: FieldCoords;
    phone: FieldCoords;
    relationship: FieldCoords;
  }> = [
    {
      name: placements.page5Contact1Name,
      phone: placements.page5Contact1Phone,
      relationship: placements.page5Contact1Relationship,
    },
    {
      name: placements.page5Contact2Name,
      phone: placements.page5Contact2Phone,
      relationship: placements.page5Contact2Relationship,
    },
  ];
  for (let i = 0; i < contactSlots.length; i++) {
    const slot = contactSlots[i]!;
    const contact = exhibit.knownContacts[i];
    if (!contact) continue;
    stampText(slot.name, contact.name, { maxWidth: 200 });
    stampText(slot.phone, contact.phone, { maxWidth: 150 });
    if (contact.relationship) {
      stampText(slot.relationship, contact.relationship, { maxWidth: 130 });
    }
  }

  // Audit footer on the last page
  const lastPage = pages[pages.length - 1]!;
  const footer = `Electronically signed by ${input.signerFullName} on ${date.toISOString()}`;
  lastPage.drawText(footer, {
    x: 36,
    y: 12,
    size: 7,
    font,
    color: rgb(0.4, 0.4, 0.4),
  });

  const bytes = await pdfDoc.save();
  return { pdfBytes: bytes, pdfBase64: uint8ToBase64(bytes) };
}

function pickPage(
  pages: ReturnType<PDFDocument["getPages"]>,
  pageIndex: number,
) {
  const idx = pageIndex < 0 ? pages.length + pageIndex : pageIndex;
  const clamped = Math.max(0, Math.min(pages.length - 1, idx));
  return pages[clamped]!;
}

function joinNonEmpty(parts: (string | null | undefined)[], sep: string): string {
  return parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter((s) => s.length > 0)
    .join(sep);
}

/** Truncates with ellipsis if `text` exceeds `maxWidth` pts at `size`. */
function clipToWidth(
  text: string,
  size: number,
  font: { widthOfTextAtSize: (s: string, sz: number) => number },
  maxWidth: number,
): string {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) return text;
  let truncated = text;
  while (
    truncated.length > 1 &&
    font.widthOfTextAtSize(`${truncated}…`, size) > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}…`;
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunk)),
    );
  }
  return btoa(binary);
}
